import { useState } from "react";
import {
  deleteCompanyFavicon,
  deleteCompanyLogo,
  getCompanySettings,
  setBrandingEnabled,
  uploadCompanyFavicon,
  uploadCompanyLogo,
} from "./api";
import { useAdminResource } from "./use-admin-resource";
import { hasFeature, useSubscription } from "./useSubscription";
import { LockedFeature } from "./LockedFeature";
import { useToast } from "../../components/useToast";
import { useConfirm } from "../../components/useConfirm";
import { env } from "../../config/env";
import { useAuth } from "../auth/useAuth";

function AssetRow({
  label,
  hasAsset,
  brandingEnabled,
  previewSrc,
  pending,
  onUpload,
  onRemove,
  accept,
}: {
  label: string;
  hasAsset: boolean;
  brandingEnabled: boolean;
  previewSrc: string;
  pending: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
  accept: string;
}) {
  return (
    <div className="action-form action-card">
      <label className="toolbar-label">{label}</label>
      <div className="checkin-fields">
        <input
          type="file"
          accept={accept}
          disabled={pending}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onUpload(file);
            event.target.value = "";
          }}
        />
        {hasAsset ? (
          <button
            className="refresh-btn danger-action-btn"
            type="button"
            disabled={pending}
            onClick={onRemove}
          >
            Remove
          </button>
        ) : null}
      </div>
      {hasAsset ? (
        brandingEnabled ? (
          <img src={previewSrc} alt={`${label} preview`} className="file-preview" />
        ) : (
          <p className="management-note">{label} uploaded — enable branding below to use it.</p>
        )
      ) : (
        <p className="management-note">No {label.toLowerCase()} uploaded yet.</p>
      )}
    </div>
  );
}

export function BrandingSettingsPanel() {
  const { session } = useAuth();
  const { data: settings, loading, error, refresh } = useAdminResource(
    () => getCompanySettings(),
    [],
  );
  const { data: subscription, loading: subscriptionLoading } = useSubscription();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [logoPending, setLogoPending] = useState(false);
  const [faviconPending, setFaviconPending] = useState(false);
  const [togglePending, setTogglePending] = useState(false);

  const companyCode = session?.companyCode ?? "";
  const cacheBust = settings?.updated_at ?? "";
  const logoSrc = `${env.apiBaseUrl}/branding/${encodeURIComponent(companyCode)}/logo?v=${encodeURIComponent(cacheBust)}`;
  const faviconSrc = `${env.apiBaseUrl}/branding/${encodeURIComponent(companyCode)}/favicon?v=${encodeURIComponent(cacheBust)}`;

  async function handleLogoUpload(file: File) {
    setLogoPending(true);
    try {
      await uploadCompanyLogo(file);
      showSuccess("Logo uploaded");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to upload logo");
    } finally {
      setLogoPending(false);
    }
  }

  async function handleLogoRemove() {
    const ok = await confirm({
      title: "Remove logo?",
      message: "The dashboard and public visitor pages will fall back to default branding.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;

    setLogoPending(true);
    try {
      await deleteCompanyLogo();
      showSuccess("Logo removed");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to remove logo");
    } finally {
      setLogoPending(false);
    }
  }

  async function handleFaviconUpload(file: File) {
    setFaviconPending(true);
    try {
      await uploadCompanyFavicon(file);
      showSuccess("Favicon uploaded");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to upload favicon");
    } finally {
      setFaviconPending(false);
    }
  }

  async function handleFaviconRemove() {
    const ok = await confirm({
      title: "Remove favicon?",
      message: "The browser tab icon will fall back to the default.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;

    setFaviconPending(true);
    try {
      await deleteCompanyFavicon();
      showSuccess("Favicon removed");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to remove favicon");
    } finally {
      setFaviconPending(false);
    }
  }

  async function handleToggle() {
    if (!settings) return;
    setTogglePending(true);
    try {
      await setBrandingEnabled(!settings.branding_enabled);
      showSuccess(settings.branding_enabled ? "Custom branding disabled" : "Custom branding enabled");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to update branding");
    } finally {
      setTogglePending(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Company Branding</h2>
        <span>{loading ? "Loading..." : settings?.branding_enabled ? "Custom" : "Default"}</span>
      </header>
      <p className="management-note">
        Upload a logo and favicon to replace the default "0todeploy VMS" branding on your
        dashboard and any pages visitors see (invites, gate passes). Uploads are kept even while
        disabled, so you can toggle back on without re-uploading.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      <LockedFeature
        feature="branding"
        featureLabel="Custom branding"
        subscription={subscription}
        loading={subscriptionLoading}
      >
        <AssetRow
          label="Logo"
          hasAsset={Boolean(settings?.has_logo)}
          brandingEnabled={Boolean(settings?.branding_enabled)}
          previewSrc={logoSrc}
          pending={logoPending}
          onUpload={(file) => void handleLogoUpload(file)}
          onRemove={() => void handleLogoRemove()}
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
        />

        <AssetRow
          label="Favicon"
          hasAsset={Boolean(settings?.has_favicon)}
          brandingEnabled={Boolean(settings?.branding_enabled)}
          previewSrc={faviconSrc}
          pending={faviconPending}
          onUpload={(file) => void handleFaviconUpload(file)}
          onRemove={() => void handleFaviconRemove()}
          accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
        />
      </LockedFeature>

      {settings?.branding_enabled || hasFeature("branding", subscription, subscriptionLoading) ? (
        <button
          className="refresh-btn action-submit"
          type="button"
          disabled={togglePending || loading}
          onClick={() => void handleToggle()}
        >
          {togglePending
            ? "Saving..."
            : settings?.branding_enabled
              ? "Disable Custom Branding"
              : "Enable Custom Branding"}
        </button>
      ) : null}
    </article>
  );
}
